import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  hoaEmailsService,
  HOAEmail,
  HOAEmailStats,
  PropertyEmailAssignment,
  DocumentType
} from '../services/hoa-emails.service';
import { propertiesService, Property } from '../services/properties.service';
import toast from 'react-hot-toast';
import {
  FiMail,
  FiUser,
  FiCalendar,
  FiMapPin,
  FiAlertCircle,
  FiCheck,
  FiX,
  FiEye,
  FiSearch,
  FiFilter,
  FiRefreshCw,
  FiBarChart2,
  FiExternalLink,
  FiTrash2
} from 'react-icons/fi';
import { tw, monoFont, hoaStatusColors, getCategoryStyle } from '../utils/terminalTheme';

export const HOAEmails: React.FC = () => {
  const [emails, setEmails] = useState<HOAEmail[]>([]);
  const [stats, setStats] = useState<HOAEmailStats | null>(null);
  const [properties, setProperties] = useState<Property[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedEmail, setSelectedEmail] = useState<HOAEmail | null>(null);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [emailViewMode, setEmailViewMode] = useState<'text' | 'html' | 'raw'>('text');
  const [assignmentModal, setAssignmentModal] = useState<{
    show: boolean;
    email: HOAEmail | null;
  }>({ show: false, email: null });
  const [deleteModal, setDeleteModal] = useState<{
    show: boolean;
    email: HOAEmail | null;
  }>({ show: false, email: null });

  // Filters
  const [filters, setFilters] = useState({
    status: '',
    priority: '',
    document_type: '',
    property_id: '',
    requires_review: '',
    search: ''
  });

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [totalEmails, setTotalEmails] = useState(0);
  const pageSize = 20;

  useEffect(() => {
    loadInitialData();
  }, []);

  useEffect(() => {
    loadEmails();
  }, [currentPage, filters]);

  const loadInitialData = async () => {
    try {
      const [statsData, propertiesData] = await Promise.all([
        hoaEmailsService.getStats(),
        propertiesService.getProperties()
      ]);

      console.log('Stats data:', statsData);
      console.log('Properties data:', propertiesData);

      setStats(statsData);
      setProperties(propertiesData);

      if (!propertiesData || propertiesData.length === 0) {
        console.warn('No properties loaded - this may cause empty dropdown');
        toast.error('No properties found. Please add properties first.');
      }
    } catch (error: any) {
      console.error('Failed to load initial data:', error);
      if (error.response) {
        console.error('Response status:', error.response.status);
        console.error('Response data:', error.response.data);
      }
      toast.error(`Failed to load HOA email data: ${error.message || 'Unknown error'}`);
    }
  };

  const loadEmails = async () => {
    try {
      setIsLoading(true);
      const response = await hoaEmailsService.getHOAEmails({
        skip: (currentPage - 1) * pageSize,
        limit: pageSize,
        status: filters.status || undefined,
        priority: filters.priority || undefined,
        document_type: filters.document_type || undefined,
        property_id: filters.property_id ? parseInt(filters.property_id) : undefined,
        requires_review: filters.requires_review === 'true' ? true : filters.requires_review === 'false' ? false : undefined,
        search: filters.search || undefined
      });

      setEmails(response.emails);
      setTotalPages(response.total_pages);
      setTotalEmails(response.total);
    } catch (error) {
      console.error('Failed to load emails:', error);
      toast.error('Failed to load HOA emails');
    } finally {
      setIsLoading(false);
    }
  };

  const handleAssignToProperty = async (emailId: number, propertyId: number, notes?: string) => {
    try {
      await hoaEmailsService.assignToProperty(emailId, {
        property_id: propertyId,
        review_notes: notes
      });

      toast.success('Email assigned to property successfully');
      setAssignmentModal({ show: false, email: null });
      await loadEmails();
      await loadInitialData(); // Refresh stats
    } catch (error) {
      console.error('Failed to assign email:', error);
      toast.error('Failed to assign email to property');
    }
  };

  const handleStatusChange = async (emailId: number, status: HOAEmail['status']) => {
    try {
      await hoaEmailsService.setStatus(emailId, status);
      toast.success('Email status updated');
      await loadEmails();
      await loadInitialData(); // Refresh stats
    } catch (error) {
      console.error('Failed to update status:', error);
      toast.error('Failed to update email status');
    }
  };

  const handlePriorityChange = async (emailId: number, priority: HOAEmail['priority']) => {
    try {
      await hoaEmailsService.setPriority(emailId, priority);
      toast.success('Email priority updated');
      await loadEmails();
    } catch (error) {
      console.error('Failed to update priority:', error);
      toast.error('Failed to update email priority');
    }
  };

  const handleDocumentTypeChange = async (emailId: number, documentType: DocumentType) => {
    try {
      await hoaEmailsService.setDocumentType(emailId, documentType);
      toast.success('Document type updated');
      await loadEmails();
    } catch (error) {
      console.error('Failed to update document type:', error);
      toast.error('Failed to update document type');
    }
  };

  const handleMarkAsReviewed = async (emailId: number, notes?: string) => {
    try {
      await hoaEmailsService.markAsReviewed(emailId, notes);
      toast.success('Email marked as reviewed');
      await loadEmails();
      await loadInitialData(); // Refresh stats
    } catch (error) {
      console.error('Failed to mark as reviewed:', error);
      toast.error('Failed to mark email as reviewed');
    }
  };

  const handleDeleteEmail = async (emailId: number) => {
    try {
      await hoaEmailsService.deleteHOAEmail(emailId);
      toast.success('Email deleted successfully');
      setDeleteModal({ show: false, email: null });
      await loadEmails();
      await loadInitialData(); // Refresh stats
    } catch (error) {
      console.error('Failed to delete email:', error);
      toast.error('Failed to delete email');
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'urgent': return 'bg-red-500/15 text-red-400 border border-red-500/30';
      case 'high': return 'bg-orange-500/15 text-orange-400 border border-orange-500/30';
      case 'medium': return 'bg-yellow-500/15 text-yellow-400 border border-yellow-500/30';
      case 'low': return 'bg-green-500/15 text-green-400 border border-green-500/30';
      default: return 'bg-gray-500/15 text-gray-400 border border-gray-500/30';
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending': return 'bg-blue-500/15 text-blue-400 border border-blue-500/30';
      case 'assigned': return 'bg-purple-500/15 text-purple-400 border border-purple-500/30';
      case 'processed': return 'bg-green-500/15 text-green-400 border border-green-500/30';
      case 'ignored': return 'bg-gray-500/15 text-gray-400 border border-gray-500/30';
      default: return 'bg-gray-500/15 text-gray-400 border border-gray-500/30';
    }
  };

  const getDocumentTypeColor = (docType: string) => {
    switch (docType) {
      case 'invoice': return 'bg-red-500/15 text-red-400 border border-red-500/30';
      case 'bill': return 'bg-orange-500/15 text-orange-400 border border-orange-500/30';
      case 'notice': return 'bg-yellow-500/15 text-yellow-400 border border-yellow-500/30';
      case 'payment_confirmation': return 'bg-green-500/15 text-green-400 border border-green-500/30';
      case 'signup_notice': return 'bg-blue-500/15 text-blue-400 border border-blue-500/30';
      case 'maintenance_notice': return 'bg-purple-500/15 text-purple-400 border border-purple-500/30';
      case 'violation_notice': return 'bg-red-500/15 text-red-400 border border-red-500/30';
      case 'meeting_notice': return 'bg-cyan-500/15 text-cyan-400 border border-cyan-500/30';
      case 'newsletter': return 'bg-teal-500/15 text-teal-400 border border-teal-500/30';
      case 'other': return 'bg-gray-500/15 text-gray-400 border border-gray-500/30';
      default: return 'bg-gray-500/15 text-gray-400 border border-gray-500/30';
    }
  };

  const getDocumentTypeDisplayName = (docType: string) => {
    switch (docType) {
      case 'invoice': return 'Invoice';
      case 'bill': return 'Bill';
      case 'notice': return 'Notice';
      case 'payment_confirmation': return 'Payment Confirmation';
      case 'signup_notice': return 'Signup Notice';
      case 'maintenance_notice': return 'Maintenance Notice';
      case 'violation_notice': return 'Violation Notice';
      case 'meeting_notice': return 'Meeting Notice';
      case 'newsletter': return 'Newsletter';
      case 'other': return 'Other';
      case 'unknown': return 'Unknown';
      default: return 'Unknown';
    }
  };

  const clearFilters = () => {
    setFilters({
      status: '',
      priority: '',
      document_type: '',
      property_id: '',
      requires_review: '',
      search: ''
    });
    setCurrentPage(1);
  };

  return (
    <div className={tw.pageWide}>
      {/* Terminal Header */}
      <div className="mb-8">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold font-mono text-white" style={monoFont}>
              <span className="text-cyan-400">~/</span>hoa-payments
              <span className="text-gray-600 text-sm font-normal ml-3">
                <span className="text-green-500">$</span> track --emails
              </span>
            </h1>
            <p className="text-gray-500 text-sm font-mono mt-1">Manage HOA statements and track payment schedules</p>
          </div>
          <button
            onClick={() => window.location.reload()}
            className={tw.btnPrimary + ' flex items-center gap-2'}
          >
            <FiRefreshCw className="w-4 h-4" />
            Refresh
          </button>
        </div>

        {/* Info Box - How HOA Payment Tracking Works */}
        <div className="mt-4 bg-gray-800/80 border border-cyan-500/20 rounded-lg p-4">
          <div className="flex items-center gap-2 mb-2">
            <FiMail className="text-cyan-400" />
            <span className="font-medium font-mono text-cyan-400 text-sm">How HOA Payment Tracking Works</span>
          </div>
          <div className="text-gray-400 text-sm font-mono leading-relaxed space-y-1">
            <p><span className="text-cyan-400 font-medium">Email Processing:</span> Forward HOA statements to hoa@gpume.com for automatic processing</p>
            <p><span className="text-cyan-400 font-medium">Property Matching:</span> AI identifies which property the HOA payment belongs to</p>
            <p><span className="text-cyan-400 font-medium">Payment Schedule:</span> Track monthly/quarterly HOA dues for each property</p>
            <p><span className="text-cyan-400 font-medium">YTD Tracking:</span> Monitor total HOA payments per property for tax purposes</p>
          </div>
        </div>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-5 gap-3 mb-6">
          <div className={tw.statCard}>
            <div className="flex items-center justify-between">
              <div>
                <p className={tw.statLabel}>Total Emails</p>
                <p className={tw.statValueCyan}>{stats.total_emails}</p>
              </div>
              <FiMail className="text-2xl text-cyan-400" />
            </div>
          </div>

          <div className={tw.statCard}>
            <div className="flex items-center justify-between">
              <div>
                <p className={tw.statLabel}>Pending</p>
                <p className="text-2xl font-bold font-mono text-blue-400">{stats.pending_emails}</p>
              </div>
              <FiAlertCircle className="text-2xl text-blue-400" />
            </div>
          </div>

          <div className={tw.statCard}>
            <div className="flex items-center justify-between">
              <div>
                <p className={tw.statLabel}>Assigned</p>
                <p className="text-2xl font-bold font-mono text-purple-400">{stats.assigned_emails}</p>
              </div>
              <FiMapPin className="text-2xl text-purple-400" />
            </div>
          </div>

          <div className={tw.statCard}>
            <div className="flex items-center justify-between">
              <div>
                <p className={tw.statLabel}>Processed</p>
                <p className="text-2xl font-bold font-mono text-green-400">{stats.processed_emails}</p>
              </div>
              <FiCheck className="text-2xl text-green-400" />
            </div>
          </div>

          <div className={tw.statCard}>
            <div className="flex items-center justify-between">
              <div>
                <p className={tw.statLabel}>Need Review</p>
                <p className="text-2xl font-bold font-mono text-orange-400">{stats.requires_review}</p>
              </div>
              <FiEye className="text-2xl text-orange-400" />
            </div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className={tw.formWrap + ' mb-6'}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-sm font-mono text-cyan-400 uppercase tracking-wider flex items-center">
            <FiFilter className="mr-2" />
            Filters
          </h3>
          <button
            onClick={clearFilters}
            className={tw.btnGhost}
          >
            Clear All
          </button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-7 gap-4">
          {/* Search */}
          <div className="md:col-span-2">
            <label className={tw.label}>Search</label>
            <div className="relative">
              <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500" />
              <input
                type="text"
                value={filters.search}
                onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                placeholder="Search emails..."
                className={tw.input + ' pl-10'}
              />
            </div>
          </div>

          {/* Status Filter */}
          <div>
            <label className={tw.label}>Status</label>
            <select
              value={filters.status}
              onChange={(e) => setFilters({ ...filters, status: e.target.value })}
              className={tw.select}
            >
              <option value="">All Status</option>
              <option value="pending">Pending</option>
              <option value="assigned">Assigned</option>
              <option value="processed">Processed</option>
              <option value="ignored">Ignored</option>
            </select>
          </div>

          {/* Priority Filter */}
          <div>
            <label className={tw.label}>Priority</label>
            <select
              value={filters.priority}
              onChange={(e) => setFilters({ ...filters, priority: e.target.value })}
              className={tw.select}
            >
              <option value="">All Priority</option>
              <option value="urgent">Urgent</option>
              <option value="high">High</option>
              <option value="medium">Medium</option>
              <option value="low">Low</option>
            </select>
          </div>

          {/* Document Type Filter */}
          <div>
            <label className={tw.label}>Document Type</label>
            <select
              value={filters.document_type}
              onChange={(e) => setFilters({ ...filters, document_type: e.target.value })}
              className={tw.select}
            >
              <option value="">All Types</option>
              <option value="invoice">Invoice</option>
              <option value="bill">Bill</option>
              <option value="notice">Notice</option>
              <option value="payment_confirmation">Payment Confirmation</option>
              <option value="signup_notice">Signup Notice</option>
              <option value="maintenance_notice">Maintenance Notice</option>
              <option value="violation_notice">Violation Notice</option>
              <option value="meeting_notice">Meeting Notice</option>
              <option value="newsletter">Newsletter</option>
              <option value="other">Other</option>
              <option value="unknown">Unknown</option>
            </select>
          </div>

          {/* Property Filter */}
          <div>
            <label className={tw.label}>
              Property ({properties.length} loaded)
            </label>
            <select
              value={filters.property_id}
              onChange={(e) => setFilters({ ...filters, property_id: e.target.value })}
              className={tw.select}
            >
              <option value="">All Properties</option>
              {properties.length === 0 ? (
                <option value="" disabled>No properties available</option>
              ) : (
                properties.map((property) => (
                  <option key={property.id} value={property.id}>
                    {property.name}
                  </option>
                ))
              )}
            </select>
          </div>

          {/* Review Filter */}
          <div>
            <label className={tw.label}>Review Status</label>
            <select
              value={filters.requires_review}
              onChange={(e) => setFilters({ ...filters, requires_review: e.target.value })}
              className={tw.select}
            >
              <option value="">All</option>
              <option value="true">Needs Review</option>
              <option value="false">Reviewed</option>
            </select>
          </div>
        </div>
      </div>

      {/* Email List */}
      <div className={tw.tableWrap}>
        <div className="px-5 py-3 border-b border-gray-700/50 flex items-center justify-between">
          <h2 className="text-sm font-mono text-gray-400 uppercase tracking-wider">
            HOA Emails ({totalEmails} total)
          </h2>
        </div>

        {isLoading ? (
          <div className="flex items-center justify-center p-8">
            <div className={tw.loading}></div>
          </div>
        ) : emails.length === 0 ? (
          <div className={tw.empty}>
            <FiMail className="mx-auto h-12 w-12 text-gray-700 mb-4" />
            <h3 className="text-sm font-mono text-white mb-1">No HOA emails found</h3>
            <p className="text-gray-500 font-mono text-xs">
              No emails match your current filters.
            </p>
          </div>
        ) : (
          <div className={tw.tableDivider}>
            {emails.map((email) => (
              <div key={email.id} className="p-5 hover:bg-gray-700/20 transition-colors group">
                <div className="flex items-start justify-between">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center space-x-3 flex-wrap gap-y-1">
                      <h4 className="text-sm font-mono font-medium text-white truncate">
                        {email.subject}
                      </h4>
                      <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-mono ${getStatusColor(email.status)}`}>
                        {email.status}
                      </span>
                      <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-mono ${getPriorityColor(email.priority)}`}>
                        {email.priority}
                      </span>
                      <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-mono ${getDocumentTypeColor(email.document_type)}`}>
                        {getDocumentTypeDisplayName(email.document_type)}
                      </span>
                      {email.requires_review && (
                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-mono bg-orange-500/15 text-orange-400 border border-orange-500/30">
                          Needs Review
                        </span>
                      )}
                      {email.has_attachments && (
                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-mono bg-cyan-500/15 text-cyan-400 border border-cyan-500/30">
                          {email.attachment_count} attachment{email.attachment_count !== 1 ? 's' : ''}
                        </span>
                      )}
                    </div>

                    <div className="mt-2 flex items-center text-xs text-gray-500 font-mono space-x-4">
                      <div className="flex items-center">
                        <FiUser className="flex-shrink-0 mr-1.5 h-3.5 w-3.5" />
                        {email.sender_name || email.sender_email}
                      </div>
                      <div className="flex items-center">
                        <FiCalendar className="flex-shrink-0 mr-1.5 h-3.5 w-3.5" />
                        {new Date(email.received_at).toLocaleDateString()}
                      </div>
                      {email.property_name && (
                        <div className="flex items-center">
                          <FiMapPin className="flex-shrink-0 mr-1.5 h-3.5 w-3.5" />
                          {email.property_name}
                        </div>
                      )}
                    </div>

                    {email.ai_suggested_property_name && !email.property_name && (
                      <div className="mt-2 p-2 bg-cyan-500/10 border border-cyan-500/20 rounded text-sm font-mono">
                        <div className="flex items-center">
                          <FiBarChart2 className="mr-2 h-4 w-4 text-cyan-400" />
                          <span className="text-cyan-400">
                            AI suggests: <strong>{email.ai_suggested_property_name}</strong>
                            ({email.ai_confidence_score} confidence)
                          </span>
                        </div>
                        {email.ai_reasoning && (
                          <p className="mt-1 text-cyan-400/70 text-xs">{email.ai_reasoning}</p>
                        )}
                      </div>
                    )}

                    {email.email_text && (
                      <div className="mt-2 text-xs text-gray-500 font-mono">
                        <p className="line-clamp-2">{email.email_text}</p>
                      </div>
                    )}
                  </div>

                  <div className="flex items-center space-x-2 ml-4">
                    <button
                      onClick={() => {
                        setSelectedEmail(email);
                        setShowEmailModal(true);
                      }}
                      className="p-2 text-gray-500 hover:text-cyan-400 transition-colors"
                      title="View Details"
                    >
                      <FiEye className="h-5 w-5" />
                    </button>

                    <button
                      onClick={() => setDeleteModal({ show: true, email })}
                      className="p-2 text-gray-500 hover:text-red-400 transition-colors"
                      title="Delete Email"
                    >
                      <FiTrash2 className="h-5 w-5" />
                    </button>

                    {!email.property_id && (
                      <button
                        onClick={() => setAssignmentModal({ show: true, email })}
                        className={tw.btnPrimary + ' text-xs px-3 py-1'}
                      >
                        Assign
                      </button>
                    )}

                    {email.requires_review && (
                      <button
                        onClick={() => handleMarkAsReviewed(email.id)}
                        className="bg-green-600 hover:bg-green-700 text-white px-3 py-1 rounded-md font-mono text-xs transition-colors"
                      >
                        Mark Reviewed
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className={tw.tableFooter}>
            <div className="text-xs text-gray-500 font-mono">
              Showing {((currentPage - 1) * pageSize) + 1} to {Math.min(currentPage * pageSize, totalEmails)} of {totalEmails} emails
            </div>
            <div className="flex space-x-2">
              <button
                onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                disabled={currentPage === 1}
                className={tw.btnSecondary + ' text-xs px-3 py-1 disabled:opacity-50 disabled:cursor-not-allowed'}
              >
                Previous
              </button>
              <span className="px-3 py-1 text-xs font-mono text-gray-400">
                Page {currentPage} of {totalPages}
              </span>
              <button
                onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                disabled={currentPage === totalPages}
                className={tw.btnSecondary + ' text-xs px-3 py-1 disabled:opacity-50 disabled:cursor-not-allowed'}
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Email Details Modal */}
      {showEmailModal && selectedEmail && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center p-4 z-50">
          <div className={tw.formWrap + ' max-w-4xl w-full max-h-[90vh] overflow-y-auto'}>
            <div>
              <div className="flex justify-between items-start mb-6">
                <div>
                  <h3 className={tw.formTitle}>Email Details</h3>
                  <div className="flex items-center space-x-2 mt-2">
                    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-mono ${getStatusColor(selectedEmail.status)}`}>
                      {selectedEmail.status}
                    </span>
                    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-mono ${getPriorityColor(selectedEmail.priority)}`}>
                      {selectedEmail.priority}
                    </span>
                    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-mono ${getDocumentTypeColor(selectedEmail.document_type)}`}>
                      {getDocumentTypeDisplayName(selectedEmail.document_type)}
                    </span>
                  </div>
                </div>
                <button
                  onClick={() => setShowEmailModal(false)}
                  className="text-gray-500 hover:text-white transition-colors"
                >
                  <FiX className="h-6 w-6" />
                </button>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Left Column - Email Metadata */}
                <div className="space-y-4">
                  <div>
                    <label className={tw.label}>Subject</label>
                    <p className="text-sm text-white font-mono p-2 bg-gray-900 rounded border border-gray-700">{selectedEmail.subject}</p>
                  </div>

                  <div>
                    <label className={tw.label}>From</label>
                    <p className="text-sm text-white font-mono">
                      {selectedEmail.sender_name ? (
                        <>
                          <span className="font-medium">{selectedEmail.sender_name}</span>
                          <br />
                          <span className="text-gray-500">&lt;{selectedEmail.sender_email}&gt;</span>
                        </>
                      ) : (
                        selectedEmail.sender_email
                      )}
                    </p>
                  </div>

                  <div>
                    <label className={tw.label}>Received</label>
                    <p className="text-sm text-white font-mono">
                      {new Date(selectedEmail.received_at).toLocaleString()}
                    </p>
                  </div>

                  <div>
                    <label className={tw.label}>Document Type</label>
                    <select
                      value={selectedEmail.document_type}
                      onChange={(e) => handleDocumentTypeChange(selectedEmail.id, e.target.value as DocumentType)}
                      className={tw.select}
                    >
                      <option value="unknown">Unknown</option>
                      <option value="invoice">Invoice</option>
                      <option value="bill">Bill</option>
                      <option value="notice">Notice</option>
                      <option value="payment_confirmation">Payment Confirmation</option>
                      <option value="signup_notice">Signup Notice</option>
                      <option value="maintenance_notice">Maintenance Notice</option>
                      <option value="violation_notice">Violation Notice</option>
                      <option value="meeting_notice">Meeting Notice</option>
                      <option value="newsletter">Newsletter</option>
                      <option value="other">Other</option>
                    </select>
                  </div>

                  {selectedEmail.property_name && (
                    <div>
                      <label className={tw.label}>Assigned Property</label>
                      <p className="text-sm text-white font-mono flex items-center">
                        <FiMapPin className="mr-1 h-4 w-4 text-cyan-400" />
                        {selectedEmail.property_name}
                      </p>
                    </div>
                  )}

                  {selectedEmail.ai_suggested_property_name && (
                    <div>
                      <label className={tw.label}>AI Suggestion</label>
                      <div className="text-sm p-2 bg-cyan-500/10 border border-cyan-500/20 rounded font-mono">
                        <p className="text-cyan-400">
                          <FiBarChart2 className="inline mr-1" />
                          <strong>{selectedEmail.ai_suggested_property_name}</strong> ({selectedEmail.ai_confidence_score})
                        </p>
                        {selectedEmail.ai_reasoning && (
                          <p className="text-cyan-400/70 text-xs mt-1">{selectedEmail.ai_reasoning}</p>
                        )}
                      </div>
                    </div>
                  )}

                  {selectedEmail.has_attachments && (
                    <div>
                      <label className={tw.label}>Attachments</label>
                      <p className="text-sm text-cyan-400 font-mono flex items-center">
                        <FiExternalLink className="mr-1 h-4 w-4" />
                        {selectedEmail.attachment_count} attachment{selectedEmail.attachment_count !== 1 ? 's' : ''}
                      </p>
                    </div>
                  )}

                  {selectedEmail.review_notes && (
                    <div>
                      <label className={tw.label}>Review Notes</label>
                      <p className="text-sm text-white font-mono p-2 bg-amber-500/10 border border-amber-500/20 rounded">{selectedEmail.review_notes}</p>
                    </div>
                  )}
                </div>

                {/* Right Column - Email Content */}
                <div className="space-y-4">
                  <div>
                    <label className={tw.label + ' mb-2'}>Email Content</label>
                    <div className="border border-gray-700 rounded-lg overflow-hidden">
                      <div className="bg-gray-900 px-3 py-2 border-b border-gray-700 flex space-x-1">
                        <button
                          onClick={() => setEmailViewMode('text')}
                          className={`px-3 py-1 text-xs font-mono rounded ${
                            emailViewMode === 'text'
                              ? 'bg-cyan-600 text-white'
                              : 'bg-gray-800 text-gray-400 hover:text-white border border-gray-700'
                          }`}
                        >
                          Text
                        </button>
                        <button
                          onClick={() => setEmailViewMode('html')}
                          className={`px-3 py-1 text-xs font-mono rounded ${
                            emailViewMode === 'html'
                              ? 'bg-cyan-600 text-white'
                              : 'bg-gray-800 text-gray-400 hover:text-white border border-gray-700'
                          }`}
                        >
                          HTML View
                        </button>
                        <button
                          onClick={() => setEmailViewMode('raw')}
                          className={`px-3 py-1 text-xs font-mono rounded ${
                            emailViewMode === 'raw'
                              ? 'bg-cyan-600 text-white'
                              : 'bg-gray-800 text-gray-400 hover:text-white border border-gray-700'
                          }`}
                        >
                          Raw Source
                        </button>
                      </div>
                      <div className="p-4 max-h-96 overflow-y-auto bg-gray-900">
                        {emailViewMode === 'text' && (
                          selectedEmail.email_text ? (
                            <div className="text-sm text-gray-300 font-mono whitespace-pre-wrap break-words">
                              {selectedEmail.email_text}
                            </div>
                          ) : (
                            <p className="text-sm text-gray-500 font-mono italic">No email content available</p>
                          )
                        )}
                        {emailViewMode === 'html' && (
                          selectedEmail.email_html ? (
                            <div className="bg-white rounded border border-gray-700">
                              <iframe
                                srcDoc={selectedEmail.email_html}
                                className="w-full h-96"
                                title="Email HTML Content"
                                sandbox="allow-same-origin"
                              />
                            </div>
                          ) : (
                            <p className="text-sm text-gray-500 font-mono italic">No HTML content available</p>
                          )
                        )}
                        {emailViewMode === 'raw' && (
                          selectedEmail.email_raw ? (
                            <pre className="text-xs text-gray-300 font-mono whitespace-pre-wrap break-all">
                              {selectedEmail.email_raw}
                            </pre>
                          ) : selectedEmail.email_text ? (
                            <pre className="text-xs text-gray-300 font-mono whitespace-pre-wrap break-all">
                              {selectedEmail.email_text}
                            </pre>
                          ) : (
                            <p className="text-sm text-gray-500 font-mono italic">No raw content available</p>
                          )
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex justify-end space-x-3 mt-6 pt-4 border-t border-gray-700/50">
                {!selectedEmail.property_id && (
                  <button
                    onClick={() => {
                      setAssignmentModal({ show: true, email: selectedEmail });
                      setShowEmailModal(false);
                    }}
                    className={tw.btnPrimary}
                  >
                    Assign to Property
                  </button>
                )}
                {selectedEmail.requires_review && (
                  <button
                    onClick={() => {
                      handleMarkAsReviewed(selectedEmail.id);
                      setShowEmailModal(false);
                    }}
                    className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-md font-mono text-sm transition-colors"
                  >
                    Mark as Reviewed
                  </button>
                )}
                <button
                  onClick={() => setShowEmailModal(false)}
                  className={tw.btnSecondary}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Assignment Modal */}
      {assignmentModal.show && assignmentModal.email && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center p-4 z-50">
          <div className={tw.formWrap + ' max-w-md w-full'}>
            <div>
              <div className="flex justify-between items-start mb-4">
                <h3 className={tw.formTitle}>Assign Email to Property</h3>
                <button
                  onClick={() => setAssignmentModal({ show: false, email: null })}
                  className="text-gray-500 hover:text-white transition-colors"
                >
                  <FiX className="h-6 w-6" />
                </button>
              </div>

              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  const formData = new FormData(e.currentTarget);
                  const propertyId = parseInt(formData.get('property_id') as string);
                  const notes = formData.get('notes') as string;

                  if (assignmentModal.email) {
                    handleAssignToProperty(assignmentModal.email.id, propertyId, notes);
                  }
                }}
                className="space-y-4"
              >
                <div>
                  <label className={tw.label}>
                    Subject: {assignmentModal.email.subject}
                  </label>
                </div>

                {assignmentModal.email.ai_suggested_property_name && (
                  <div className="p-3 bg-cyan-500/10 border border-cyan-500/20 rounded font-mono">
                    <p className="text-sm text-cyan-400">
                      <FiBarChart2 className="inline mr-1" />
                      AI suggests: <strong>{assignmentModal.email.ai_suggested_property_name}</strong>
                    </p>
                    {assignmentModal.email.ai_reasoning && (
                      <p className="text-xs text-cyan-400/70 mt-1">{assignmentModal.email.ai_reasoning}</p>
                    )}
                  </div>
                )}

                <div>
                  <label className={tw.label}>
                    Property ({properties.length} available)
                  </label>
                  <select
                    name="property_id"
                    required
                    defaultValue={assignmentModal.email.ai_suggested_property_id || ''}
                    className={tw.select}
                  >
                    <option value="">Select a property</option>
                    {properties.length === 0 ? (
                      <option value="" disabled>No properties available</option>
                    ) : (
                      properties.map((property) => (
                        <option key={property.id} value={property.id}>
                          {property.name}
                        </option>
                      ))
                    )}
                  </select>
                </div>

                <div>
                  <label className={tw.label}>Notes (optional)</label>
                  <textarea
                    name="notes"
                    rows={3}
                    className={tw.input}
                    placeholder="Add any notes about this assignment..."
                  />
                </div>

                <div className="flex space-x-3">
                  <button
                    type="submit"
                    className={tw.btnPrimary + ' flex-1'}
                  >
                    Assign
                  </button>
                  <button
                    type="button"
                    onClick={() => setAssignmentModal({ show: false, email: null })}
                    className={tw.btnSecondary + ' flex-1'}
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteModal.show && deleteModal.email && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center p-4 z-50">
          <div className={tw.formWrap + ' max-w-md w-full'}>
            <div>
              <div className="flex justify-between items-start mb-4">
                <h3 className={tw.formTitle}>Delete Email</h3>
                <button
                  onClick={() => setDeleteModal({ show: false, email: null })}
                  className="text-gray-500 hover:text-white transition-colors"
                >
                  <FiX className="h-6 w-6" />
                </button>
              </div>

              <div className="mb-6">
                <div className="flex items-center mb-3">
                  <div className="flex-shrink-0 w-10 h-10 mx-auto flex items-center justify-center rounded-full bg-red-500/15 border border-red-500/30">
                    <FiTrash2 className="w-6 h-6 text-red-400" />
                  </div>
                </div>
                <div className="text-center">
                  <h3 className="text-sm font-mono font-medium text-white mb-2">
                    Are you sure you want to delete this email?
                  </h3>
                  <p className="text-xs text-gray-500 font-mono mb-4">
                    <strong className="text-gray-400">Subject:</strong> {deleteModal.email.subject}
                  </p>
                  <p className="text-xs text-gray-500 font-mono">
                    This action cannot be undone. The email and any associated data will be permanently removed.
                  </p>
                </div>
              </div>

              <div className="flex space-x-3">
                <button
                  onClick={() => setDeleteModal({ show: false, email: null })}
                  className={tw.btnSecondary + ' flex-1'}
                >
                  Cancel
                </button>
                <button
                  onClick={() => handleDeleteEmail(deleteModal.email!.id)}
                  className="flex-1 px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-md font-mono text-sm transition-colors"
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
